import {
  Home,
  Send,
  Users,
  Search,
  Briefcase,
  BarChart3,
  CreditCard,
  Calendar,
  Settings,
  Mail,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface VisibilityRules {
  /** Hide this item when the organization is a brand org */
  hideForBrandOrg?: boolean;
  /** Only show this item to admin users */
  requireAdmin?: boolean;
}

export interface SidebarMenuItem {
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;
  visibilityRules?: VisibilityRules;
}

export interface SidebarSection {
  title: string;
  items: SidebarMenuItem[];
}

export const sidebarSections: SidebarSection[] = [
  {
    title: 'Overview',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: Home, path: '/' },
      { id: 'campaigns', label: 'Campaigns', icon: Send, path: '/campaigns' },
      { id: 'influencers', label: 'Influencers', icon: Users, path: '/influencers' },
      { id: 'discovery', label: 'Discovery', icon: Search, path: '/discovery' },
      {
        id: 'brands',
        label: 'Brands',
        icon: Briefcase,
        path: '/brands',
        visibilityRules: { hideForBrandOrg: true },
      },
    ],
  },
  {
    title: 'Communication',
    items: [
      { id: 'email-center', label: 'Email Center', icon: Mail, path: '/email-center' },
      { id: 'calendar', label: 'Calendar', icon: Calendar, path: '/calendar' },
    ],
  },
  {
    title: 'Analytics & Finance',
    items: [
      { id: 'analytics', label: 'Analytics', icon: BarChart3, path: '/analytics' },
      { id: 'payments', label: 'Payments', icon: CreditCard, path: '/payments' },
    ],
  },
  {
    title: 'Admin',
    items: [
      {
        id: 'user-management',
        label: 'User Management',
        icon: Users,
        path: '/user-management',
        visibilityRules: { requireAdmin: true },
      },
      { id: 'settings', label: 'Settings', icon: Settings, path: '/settings' },
    ],
  },
];

export const sidebarMenuItems: SidebarMenuItem[] = sidebarSections.flatMap(
  (section) => section.items
);

/**
 * Filters menu sections and items based on the current user's context.
 */
export const getVisibleMenuSections = (context: {
  isBrandOrg: boolean;
  isAdmin: boolean;
}): SidebarSection[] => {
  return sidebarSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        const rules = item.visibilityRules;
        if (!rules) return true;
        if (rules.hideForBrandOrg && context.isBrandOrg) return false;
        if (rules.requireAdmin && !context.isAdmin) return false;
        return true;
      }),
    }))
    .filter((section) => section.items.length > 0);
};

/**
 * Filters flat menu items based on the current user's context.
 */
export const getVisibleMenuItems = (context: {
  isBrandOrg: boolean;
  isAdmin: boolean;
}): SidebarMenuItem[] => {
  return getVisibleMenuSections(context).flatMap((section) => section.items);
};
